/**
 * realtimeConsoleApi — REST 보조 API 클라이언트
 *
 * 엔드포인트:
 *   GET /realtime-console/health   — 헬스 (AUTHENTICATED)
 *   GET /realtime-console/messages — 최근 메시지 목록 (REALTIME_CONSOLE_READ)
 *   GET /realtime-console/sessions — 접속 세션 목록 (REALTIME_CONSOLE_READ)
 *
 * 패턴: report-builder/api/reportBuilderApi.ts 와 동일
 *   - axios 인스턴스 생성
 *   - attachAuthInterceptors 로 401 세션 만료 처리
 *   - ApiResponse<T> 언래핑 인터셉터
 *   - Bearer 토큰 Request 인터셉터
 */
import axios from 'axios';
import type { ConsoleHealth, ChatMessageView, SessionInfo } from '../types/realtimeConsole';
import { getStoredToken } from '../../login/api/loginApi';
import { attachAuthInterceptors } from '../../_shared/auth/authInterceptor';

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE ?? '/api/eraf',
  headers: { 'Content-Type': 'application/json' },
});

attachAuthInterceptors(apiClient);

// Bearer 토큰 Request 인터셉터
apiClient.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// ApiResponse<T> 언래핑 Response 인터셉터
apiClient.interceptors.response.use(
  (response) => {
    const d = response.data as Record<string, unknown> | undefined;
    if (d && typeof d === 'object' && 'success' in d) {
      if (d.success === true) {
        return { ...response, data: d['data'] };
      }
      const err = d['error'] as Record<string, unknown> | undefined;
      return Promise.reject({
        code: err?.['code'] ?? `HTTP-${response.status}`,
        message: err?.['message'] ?? '알 수 없는 오류',
        traceId: (err?.['traceId'] as string) ?? response.headers?.['x-trace-id'],
      });
    }
    return response;
  },
  (error) => {
    const errData = error.response?.data?.error as Record<string, unknown> | undefined;
    const code = errData?.['code'] ?? `HTTP-${error.response?.status ?? 0}`;
    const message =
      (errData?.['message'] as string) ??
      (error.response?.data?.message as string) ??
      error.message ??
      '서버에 연결할 수 없습니다';
    const traceId = (error.response?.headers?.['x-trace-id'] as string) ?? undefined;
    return Promise.reject({ code, message, traceId });
  }
);

export const realtimeConsoleApi = {
  /**
   * GET /realtime-console/health — 실시간 콘솔 헬스
   * AUTHENTICATED (모든 인증 사용자 허용)
   */
  health: (): Promise<ConsoleHealth> =>
    apiClient
      .get<unknown, { data: ConsoleHealth }>('/realtime-console/health')
      .then((res) => res.data),

  /**
   * GET /realtime-console/messages — 최근 메시지 목록 (휘발성 ring buffer)
   * REALTIME_CONSOLE_READ 필요
   */
  messages: (room = 'lobby'): Promise<ChatMessageView[]> =>
    apiClient
      .get<unknown, { data: ChatMessageView[] }>(`/realtime-console/messages?room=${encodeURIComponent(room)}`)
      .then((res) => res.data),

  /**
   * GET /realtime-console/sessions — 현재 접속 세션 목록
   * REALTIME_CONSOLE_READ 필요
   */
  sessions: (): Promise<SessionInfo[]> =>
    apiClient
      .get<unknown, { data: SessionInfo[] }>('/realtime-console/sessions')
      .then((res) => res.data),
};
